/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  WorkspaceFilterColumns,
  WorkspaceFilterModel,
  useSetWorkspaceFilter,
  useValidateWorkspaceListOrder,
  useWorkspaceFilter,
} from '@gripp/shared-logic';
import {
  ColumnSelection,
  DropdownFooter,
  FilterType,
  FormContext,
  filterStyles as styles,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ScrollView, View } from 'react-native';
import { array, string, object as yupObject } from 'yup';

type WorkspaceFilterProps = {
  onCloseSideBar: () => void;
  type: FilterType;
};

export const WorkspaceFilter = (props: WorkspaceFilterProps) => {
  const { data: workspaceFilter } = useWorkspaceFilter();
  const { mutateAsync: setWorkspaceFilter } = useSetWorkspaceFilter();
  const validateListOrder = useValidateWorkspaceListOrder();

  const formSchema = yupObject({
    columns: array().of(string().optional().nullable()).optional().nullable(),
  });
  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      columns: workspaceFilter?.columns,
    },
  });

  const {
    formState: { isDirty },
    getValues,
    handleSubmit,
    reset,
    watch,
  } = formMethods;

  useEffect(() => {
    reset({
      columns: workspaceFilter?.columns,
    });
  }, [workspaceFilter, reset]);

  const selectedColumns = watch('columns');

  const isColumnsFilter = props.type === FilterType.WORKSPACECOLUMNS;

  const resetForm = useCallback(() => {
    reset({
      ...selectedColumns,
      ...(isColumnsFilter && { columns: new WorkspaceFilterModel().columns }),
    });
  }, [isColumnsFilter, reset, selectedColumns]);

  const canReset = (): boolean => {
    return !Object.values(getValues()).every((value) => value === undefined);
  };

  const applyFilter = async (filterModel: any) => {
    await Promise.all([
      setWorkspaceFilter(filterModel),
      validateListOrder(filterModel),
    ]);

    props.onCloseSideBar();
  };

  const onReset = async () => {
    const model = new WorkspaceFilterModel();
    const updatedFields = {
      ...(isColumnsFilter && { columns: model.columns }),
    };

    resetForm();

    const mergedFilters = { ...workspaceFilter, ...updatedFields };
    await applyFilter(new WorkspaceFilterModel(mergedFilters));
  };

  const onSubmit = async (data: any) => {
    const model = new WorkspaceFilterModel({
      ...workspaceFilter,
      ...data,
    });
    await applyFilter(model);
  };

  const renderStandardData = () => {
    return (
      <ColumnSelection
        baseColumns={WorkspaceFilterColumns}
        i18nPrefix="workspace.filter"
      />
    );
  };

  return (
    <FormContext.Provider value={formMethods}>
      <View style={styles.container}>
        <View style={[styles.scrollViewContainer, { marginBottom: 30 }]}>
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
          >
            {isColumnsFilter && renderStandardData()}
          </ScrollView>
        </View>
        <DropdownFooter
          isDirty={isDirty}
          onSubmit={handleSubmit(onSubmit)}
          canReset={canReset}
          onReset={onReset}
        />
      </View>
    </FormContext.Provider>
  );
};
